import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { addToCart } from '../../store/slices/cartSlice';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, Button, CircularProgress
} from '@mui/material';
import { CheckCircle, Cancel, ShoppingCart } from '@mui/icons-material';
import { toast } from 'react-toastify';

const CustomerQuotes = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadQuotes(); }, []);

  const loadQuotes = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/quotes');
      setQuotes(data.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleAccept = async (quoteId) => {
    try {
      await api.put(`/quotes/${quoteId}/accept`);
      loadQuotes();
    } catch (err) { console.error(err); }
  };

  const handleReject = async (quoteId) => {
    try {
      await api.put(`/quotes/${quoteId}/reject`);
      loadQuotes();
    } catch (err) { console.error(err); }
  };

  const handleAddToCart = (quote) => {
    dispatch(addToCart({
      productId: quote.product?._id || quote.product,
      name: quote.product?.name || 'Quoted Product',
      nameAr: quote.product?.nameAr,
      osesgCode: quote.product?.osesgCode || '',
      image: quote.product?.images?.find(i => i.isPrimary)?.url || quote.product?.images?.[0]?.url,
      unitPrice: quote.osesgPrice,
      quantity: quote.quantity || 1,
      supplierId: quote.product?.supplier,
      moq: quote.product?.pricing?.moq || 1,
      quoteId: quote._id
    }));
    toast.success('Added to cart! Redirecting to checkout...');
    navigate('/customer/cart');
  };

  const statusColors = {
    pending: 'warning', supplier_responded: 'info', admin_reviewed: 'primary',
    sent_to_customer: 'secondary', accepted: 'success', rejected: 'error'
  };

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>{t('my_quotes')}</Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell>Quote #</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell>OSESG Price</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {quotes.map((quote) => (
              <TableRow key={quote._id} hover>
                <TableCell><strong>{quote.quoteNumber}</strong></TableCell>
                <TableCell>{quote.product?.name || 'N/A'}</TableCell>
                <TableCell>{quote.quantity}</TableCell>
                <TableCell>
                  {quote.osesgPrice ? `$${quote.osesgPrice.toLocaleString()}` : 'Pending'}
                </TableCell>
                <TableCell>
                  <Chip label={quote.status} size="small" color={statusColors[quote.status] || 'default'} />
                </TableCell>
                <TableCell>{new Date(quote.createdAt).toLocaleDateString()}</TableCell>
                <TableCell>
                  {quote.status === 'sent_to_customer' && (
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      <Button size="small" color="success" startIcon={<CheckCircle />}
                        onClick={() => handleAccept(quote._id)}>
                        Accept
                      </Button>
                      <Button size="small" color="error" startIcon={<Cancel />}
                        onClick={() => handleReject(quote._id)}>
                        Reject
                      </Button>
                    </Box>
                  )}
                  {quote.status === 'accepted' && quote.osesgPrice && (
                    <Button size="small" variant="contained" color="secondary"
                      startIcon={<ShoppingCart />}
                      onClick={() => handleAddToCart(quote)}>
                      Order Now
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {quotes.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" py={2}>No quotes yet</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CustomerQuotes;
